
import Vue from 'vue';
import { Validator, ValidationProvider, ValidationObserver } from 'vee-validate';
import { StripeConfigConstants, StripeCustomer } from "@/interfaces/Stripe";
import { PropType } from 'vue';
import { Company } from '@/interfaces/company';
import {http} from "@/config/httpCommon";
import { handleResponseException, handleResponseErrors } from '@/helpers/Helpers';
import { santaClone } from '@/helpers/Helpers';
import { StripeInvoiceTemplate } from '@/interfaces/stripeInvoiceTemplate';
import { LabelCodePair, LabelValuePair } from '@/interfaces/general';

export default Vue.extend({ 
  name: "editCompanyModal",
  components: { ValidationProvider, ValidationObserver },
  props: {
    priceLists: {
      type: Array,
      required: true,
    },
    stripeConfigConstants: {
      type: Object as PropType<StripeConfigConstants>,
      required: true,
    },
    companyEdit: {
      type: Object as PropType<Company>,
      required: true,
    },
    stripeCanadianInvoiceTemplates: {
      type: Array as PropType<StripeInvoiceTemplate[]>,
      required: true,
    },
    stripeDefaultInvoiceTemplates: {
      type: Array as PropType<StripeInvoiceTemplate[]>,
      required: true,
    },
    stripeCanadianCustomers: {
      type: Array as PropType<StripeCustomer[]>,
      required: true,
    },
    stripeDefaultCustomers: {
      type: Array as PropType<StripeCustomer[]>,
      required: true,
    },
    countries: {
      type: Array as PropType<Company['country'][]>,
      required: true,
    },
  },
  computed: {
    overrideMerchant(): boolean {
       return this.companyEdit.billing_data.override_company_details;
    },
    stripeTaxIdTypes(): Company['country']['stripe_tax_id_types'][] {
      if(this.overrideMerchant) {
        return this.companyEdit.billing_data.country?.stripe_tax_id_types || [];
      } else {
        return this.companyEdit.country?.stripe_tax_id_types || [];
      }
    },
    stripeCustomers(): StripeCustomer[] {
      const stripeApiIntegration = this.companyEdit.billing_data.stripe_api_integration.value;
      return stripeApiIntegration === "CANADIAN" ? this.stripeCanadianCustomers : this.stripeDefaultCustomers;
    }
  },
  watch: {
    overrideMerchant(newVal: boolean) {
      this.companyEdit.billing_data.stripe_tax_code = null;
      this.companyEdit.billing_data.tax_identifier = null;
    }
  },
  async created() {
    this.beforeUpdateData = {
      invoice_template_id: this.companyEdit.billing_data.invoice_template_id,
      country_id: this.companyEdit.country_id,
      region: this.companyEdit.region,
      billing_country_id: this.companyEdit.billing_data.country_id,
      billing_region: this.companyEdit.billing_data.region,
      stripe_integration: this.companyEdit.billing_data.stripe_api_integration.value,
    };
    this.setInvoiceTemplates();
    this.setSelectedIntegerationCountries();
    this.fetchBuyingGroups();
    this.fetchExternalSalesReps();
    this.merchantBillingEmail = this.companyEdit.settings.find(setting => setting.key_name === "merchant_billing_email")?.value?.email || null;
    await this.fetchCSManagers();
    await this.fetchSalesReps();
    this.setSelectedCSManager();
    this.setSelectedSalesRep();
  },
  data() {
    return {
      showLoader: false, 
      beforeUpdateData: {
        invoice_template_id: null,
        country_id: null,
        region: null,
        billing_country_id: null,
        billing_region: null,
      },
      stripeInvoiceTemplates: [] as StripeInvoiceTemplate[],
      selectedIntegerationCountries: [] as (LabelCodePair & {id: number})[],
      selectedStripeCustomer: null as StripeCustomer | null,
      merchantBillingEmail: null as string,

      csManagerOptions: [] as {id: number, label: string}[],
      selectedCSManager: null as {id: number, label: string} | null,
      salesRepOptions: [] as {id: number, label: string}[],
      selectedSalesRep: null as {id: number, label: string} | null,
      csManagerLoading: false,
      salesRepLoading: false,

      buyingGroupOptions: [],
      externalSalesRepOptions: [],

    };
  },
  methods: {
    async fetchBuyingGroups() {
      try {
        const response = await http.get('/buying-groups-sales-reps/buying-groups');
        if (response.data.success) {
          this.buyingGroupOptions = response.data.data;
        }
      } catch (error) {
        console.error('Failed to fetch buying groups:', error);
      }
    },
    
    async fetchExternalSalesReps() {
      try {
        const response = await http.get('/buying-groups-sales-reps/external-sales-reps');
        if (response.data.success) {
          this.externalSalesRepOptions = response.data.data;
        }
      } catch (error) {
        console.error('Failed to fetch external sales reps:', error);
      }
    },
    handleBuyingGroupChange(selectedGroup: any) {
      // Keep as array but with single selection
      if (selectedGroup) {
        this.companyEdit.buying_groups = [selectedGroup];
      } else {
        this.companyEdit.buying_groups = [];
      }
    },
    
    handleExternalSalesRepChange(selectedRep: any) {
      // Keep as array but with single selection
      if (selectedRep) {
        this.companyEdit.external_sales_reps = [selectedRep];
      } else {
        this.companyEdit.external_sales_reps = [];
      }
    },
    updateCompany() {
      this.$refs.companyEditFormObserver.validate().then(async valid => {
        if (valid) {
          const companyUpdatePayload = this.getCompanyUpdatePayload();
          this.showLoader = true;
          await http.put('/companies/'+this.companyEdit.id, companyUpdatePayload).then(res => {
            this.$emit('company-updated', res.data.result);
            this.showLoader = false;
          }).catch((errorResponse) => {
            this.showLoader = false;
            this.handleResponseErrors(errorResponse)
          });
        }
      });
    },
    getCompanyUpdatePayload() {
      const updatedCompany = santaClone(this.companyEdit);
      let stripeId = updatedCompany.billing_data.stripe_id || null;
      if(this.selectedStripeCustomer) {
        stripeId = this.selectedStripeCustomer.id;
      }
      let companyCountry = {
        id: updatedCompany.country.id,
        label: updatedCompany.country.label,
        code: updatedCompany.country.code,
      };
      let billingCountry = null
      if(this.companyEdit.billing_data.country) {
        billingCountry = {
          id: updatedCompany.billing_data.country.id,
          label: updatedCompany.billing_data.country.label,
          code: updatedCompany.billing_data.country.code,
        };
      }
      // let companyCountry = 
      const companyUpdatedPayload = {
        company_name: updatedCompany.company_name,
        address: updatedCompany.address,
        country: companyCountry,
        region: updatedCompany.region,
        city: updatedCompany.city,
        post_code: updatedCompany.post_code,
        company_type: updatedCompany.company_type,
        phone: updatedCompany.phone,
        pricelist_id: updatedCompany.pricelist.id,
        merchant_billing_email: this.merchantBillingEmail,

        cs_manager_id: this.selectedCSManager ? this.selectedCSManager.id : null,
        sales_representative_id: this.selectedSalesRep ? this.selectedSalesRep.id : null,

        buying_group_ids: updatedCompany.buying_groups?.map(bg => bg.id) || [],
        external_sales_rep_ids: updatedCompany.external_sales_reps?.map(sr => sr.id) || [],

        billing_data: {
          override_company_details: updatedCompany.billing_data.override_company_details,
          payment_method: updatedCompany.billing_data.payment_method.value,
          payment_days_until_due: updatedCompany.billing_data.payment_days_until_due,
          tax_identifier: updatedCompany.billing_data.tax_identifier || null,
          stripe_tax_code: updatedCompany.billing_data.stripe_tax_code?.tax_id_type || null,
          stripe_api_integration: updatedCompany.billing_data.stripe_api_integration.value,
          invoice_template_id: updatedCompany.billing_data.invoice_template_id.id,
          address1: updatedCompany.billing_data.address1,
          address2: updatedCompany.billing_data.address2,
          name: updatedCompany.billing_data.name,
          country: billingCountry,
          region: updatedCompany.billing_data.region,
          city: updatedCompany.billing_data.city,
          zip_code: updatedCompany.billing_data.zip_code,
          stripe_id: stripeId,
          tax_status: updatedCompany.billing_data.tax_status,
        }
      }
      return companyUpdatedPayload;
    },
    handleOverrideMerchantChange(updatedValue: boolean) {
      this.$refs.companyEditFormObserver.validate();
    },
    async handleStripeIntegrationChange(updatedIntegeration: LabelValuePair) {
      const stripeIntegerationUpdated = this.beforeUpdateData.stripe_integration !== updatedIntegeration.value;
      let stripeIntegerationChangeConfirmation = true;
      if(stripeIntegerationUpdated) {
        stripeIntegerationChangeConfirmation = await this.$confirm("Stripe Integration has been changed. Please confirm to proceed.", null, null, {allowOutsideClick: false}).catch(() => false);
      }
      if(stripeIntegerationChangeConfirmation) {
       this.companyEdit.billing_data.stripe_api_integration = updatedIntegeration;
       this.selectedStripeCustomer = null;
       this.setInvoiceTemplates();
       this.setSelectedIntegerationCountries();
      }
    },
    setInvoiceTemplates() {
      const stripeApiIntegration = this.companyEdit.billing_data.stripe_api_integration.value;
      this.stripeInvoiceTemplates = stripeApiIntegration === "CANADIAN" ? this.stripeCanadianInvoiceTemplates : this.stripeDefaultInvoiceTemplates;
      this.companyEdit.billing_data.invoice_template_id = this.stripeInvoiceTemplates.find(invoiceTemplate => invoiceTemplate.id === this.beforeUpdateData.invoice_template_id) || null;
    },
    setSelectedIntegerationCountries() {
      const stripeApiIntegration = this.companyEdit.billing_data.stripe_api_integration.value;
      if(stripeApiIntegration === "CANADIAN") {
        this.selectedIntegerationCountries = this.countries.filter(country => country.id === 39);
      } else {
        this.selectedIntegerationCountries = this.countries.filter(country => country.id !== 39);
      }
      this.companyEdit.country = this.selectedIntegerationCountries.find((country: LabelCodePair & {id: number}) => country.id === this.beforeUpdateData.country_id) || null;

      this.companyEdit.country_id = this.companyEdit.country ? this.companyEdit.country.id : null;
      this.companyEdit.billing_data.country = this.selectedIntegerationCountries.find((country: LabelCodePair & {id: number}) => country.id === this.beforeUpdateData.billing_country_id) || null;
      this.companyEdit.billing_data.country_id = this.companyEdit.billing_data.country ? this.companyEdit.billing_data.country.id : null;
      this.setSelectedCountryState();
      
    },
    handleCountryChange(type: 'company_country' | 'billing_country', country: Company['country']  ) {
      if(type === 'company_country') {
        this.companyEdit.country_id = country.id;
      } else {
        this.companyEdit.billing_data.country_id = country.id;
      }
      this.setSelectedCountryState();
    },
    setSelectedCountryState() {
      this.$nextTick(() => {
        if(this.companyEdit.country?.id && this.companyEdit.country.states.length > 0 && this.beforeUpdateData.region) {
          this.companyEdit.region = this.companyEdit.country.states.find(state => state.code === this.beforeUpdateData?.region?.code) || null;
        } else {
          this.companyEdit.region = null;
        }
        if(this.companyEdit.billing_data?.country?.id && this.companyEdit.billing_data.country.states.length > 0 && this.beforeUpdateData.billing_region) {
          this.companyEdit.billing_data.region = this.companyEdit.billing_data.country.states.find(state => state.code === this.beforeUpdateData?.billing_region?.code) || null;
        } else {
          this.companyEdit.billing_data.region = null;
        }
      });
    },
    async fetchCSManagers() {
      try {
        this.csManagerLoading = true;
        const res = await http.get(`role/users?role=csManager`);
        if (res.data.result && res.data.result.users) {
          interface UserResponse {
            id: number;
            name?: string;
            email?: string;
          }
          this.csManagerOptions = res.data.result.users.map((user: UserResponse) => ({
            id: user.id,
            label: user.name || user.email || ''
          }));
        }
      } catch (error) {
        console.error('Error fetching CS Managers:', error);
        handleResponseException(error);
      } finally {
        this.csManagerLoading = false;
      }
    },
    async fetchSalesReps() {
      try {
        this.salesRepLoading = true;
        const res = await http.get(`role/users?role=salesRepresentative`);
        if (res.data.result && res.data.result.users) {
          interface UserResponse {
            id: number;
            name?: string;
            email?: string;
          }
          this.salesRepOptions = res.data.result.users.map((user: UserResponse) => ({
            id: user.id,
            label: user.name || user.email || ''
          }));
        }
      } catch (error) {
        console.error('Error fetching Sales Representatives:', error);
        handleResponseException(error);
      } finally {
        this.salesRepLoading = false;
      }
    },
    setSelectedCSManager() {
      // Check if company has a CS Manager assigned
      // The company object might have cs_manager or cs_manager_id field
      // Adjust this based on your actual API response structure
      interface CompanyWithCSManager extends Company {
        cs_manager_id?: number;
        cs_manager?: { id: number };
      }
      const company = this.companyEdit as CompanyWithCSManager;
      if (company.cs_manager_id) {
        const csManagerId = company.cs_manager_id;
        this.selectedCSManager = this.csManagerOptions.find(manager => manager.id === csManagerId) || null;
      } else if (company.cs_manager) {
        const csManager = company.cs_manager;
        this.selectedCSManager = this.csManagerOptions.find(manager => manager.id === csManager.id) || null;
      }
    },
    setSelectedSalesRep() {
      interface CompanyWithSalesRep extends Company {
        sales_representative_id?: number;
        sales_representative?: { id: number };
      }
      const company = this.companyEdit as CompanyWithSalesRep;
      if (company.sales_representative_id) {
        const repId = company.sales_representative_id;
        this.selectedSalesRep = this.salesRepOptions.find(rep => rep.id === repId) || null;
      } else if (company.sales_representative) {
        const rep = company.sales_representative;
        this.selectedSalesRep = this.salesRepOptions.find(r => r.id === rep.id) || null;
      }
    }
  }
});
